import { isOptionFieldType, pickT } from "@repo/shared";
import {
  FLOW_FIELD_SCOPES,
  type FlowField,
  type FlowFieldOptionInput,
  type FlowFieldScope,
} from "@repo/shared/types";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ApiError, apiErrorCode, patch, post } from "@/lib/api/_client";
import type { FieldType } from "@/lib/types/flow";
import { cn } from "@/lib/utils";
import { T } from "./flow-field-dialog.i18n";
import {
  FlowFieldOptionRow,
  type FlowFieldOptionRowLabels,
  type OptionEditMode,
} from "./flow-field-option-row";

// Keyed by the shared scope list, so a new scope fails the type check until it has a label.
const SCOPE_LABEL_KEYS: Record<FlowFieldScope, keyof typeof T.en_GB> = {
  global: "globalScope",
  goal: "goalScope",
  portfolio: "portfolioScope",
};

type FlowFieldDialogProps = {
  flowId: string;
  flowLanguage: string;
  field: FlowField | null;
  /** All fields of the flow — for the inline duplicate sync-key check. */
  fields: FlowField[];
  open: boolean;
  onClose: (success: boolean) => void;
};

// Existing options keep their id so label edits stay in place (QL-201);
// `key` is a stable local identity for React keys.
type OptionRow = { key: string; id?: string; label: string; syncKey: string };

export function FlowFieldDialog({
  flowId,
  flowLanguage,
  field,
  fields,
  open,
  onClose,
}: FlowFieldDialogProps) {
  const t = pickT(T, flowLanguage);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState("");
  const [type, setType] = useState<FieldType>("text");
  const [scope, setScope] = useState<FlowFieldScope>("global");
  const [isRequired, setIsRequired] = useState(false);
  const [syncKey, setSyncKey] = useState("");
  const [options, setOptions] = useState<OptionRow[]>([]);
  // What the option rows edit — all of them at once.
  const [optionMode, setOptionMode] = useState<OptionEditMode>("label");

  const FIELD_TYPES: { value: FieldType; label: string }[] = [
    { value: "text", label: t.text },
    { value: "number", label: t.number },
    { value: "dropdown", label: t.dropdown },
    { value: "radio", label: t.radio },
    { value: "multiselect", label: t.multiselect },
    { value: "boolean", label: t.boolean },
    { value: "textarea", label: t.textArea },
    { value: "date", label: t.date },
    { value: "datetime", label: t.datetime },
    { value: "email", label: t.email },
    { value: "phone", label: t.phone },
  ];

  useEffect(() => {
    if (open) {
      if (field) {
        setName(field.name);
        setType(field.type as FieldType);
        setScope(field.scope || "global");
        setIsRequired(field.isRequired);
        setSyncKey(field.syncKey ?? "");
        setOptions(
          isOptionFieldType(field.type)
            ? (field.options ?? [])
                .filter((option) => !option.isArchived)
                .map((option) => ({
                  key: option.id,
                  id: option.id,
                  label: option.label,
                  syncKey: option.syncKey ?? "",
                }))
            : [],
        );
      } else {
        setName("");
        setType("text");
        setScope("global");
        setIsRequired(false);
        setSyncKey("");
        setOptions([]);
      }
      setOptionMode("label");
      setError(null);
      setLoading(false); // Reset loading state when dialog opens
    }
  }, [field, open]);

  const handleAddOption = () => {
    setOptions([...options, { key: crypto.randomUUID(), label: "", syncKey: "" }]);
    setError(null);
  };

  const handleRemoveOption = (index: number) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  // Swap with the neighbour; the saved order is simply the row order.
  const handleMoveOption = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= options.length) return;
    const next = [...options];
    [next[index], next[target]] = [next[target]!, next[index]!];
    setOptions(next);
  };

  const handleOptionChange = (
    index: number,
    patch: Partial<Pick<OptionRow, "label" | "syncKey">>,
  ) => {
    setOptions(options.map((option, i) => (i === index ? { ...option, ...patch } : option)));
  };

  const handleTypeChange = (newType: FieldType) => {
    setType(newType);
    if (!isOptionFieldType(newType)) setOptions([]);
  };

  // UX only — the server's unique index is the source of truth (409).
  const trimmedSyncKey = syncKey.trim();
  const syncKeyTaken =
    trimmedSyncKey !== "" &&
    fields.some((other) => other.id !== field?.id && other.syncKey === trimmedSyncKey);

  // Option keys hang off the field's key: without one they are not editable
  // and are dropped on save (the server rejects them).
  const optionSyncKeysEnabled = trimmedSyncKey !== "";
  const optionKeyCounts = new Map<string, number>();
  for (const option of options) {
    const key = option.syncKey.trim();
    if (key) optionKeyCounts.set(key, (optionKeyCounts.get(key) ?? 0) + 1);
  }
  const isOptionKeyTaken = (option: OptionRow) =>
    optionSyncKeysEnabled && (optionKeyCounts.get(option.syncKey.trim()) ?? 0) > 1;
  const anyOptionKeyTaken = options.some(isOptionKeyTaken);
  const optionKeysDropped =
    !optionSyncKeysEnabled && options.some((option) => option.syncKey.trim() !== "");
  // Clearing the field's key falls back to labels without losing the choice.
  const effectiveOptionMode: OptionEditMode = optionSyncKeysEnabled ? optionMode : "label";

  const optionRowLabels: FlowFieldOptionRowLabels = {
    optionLabel: t.optionLabel,
    moveOptionUp: t.moveOptionUp,
    moveOptionDown: t.moveOptionDown,
    removeOption: t.removeOption,
    optionSyncKeyPlaceholder: t.optionSyncKeyPlaceholder,
    optionSyncKeyAria: t.optionSyncKeyAria,
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError(t.fieldNameRequired);
      return;
    }

    if (isOptionFieldType(type)) {
      if (options.length === 0) {
        setError(t.dropdownMustHaveOptions);
        return;
      }
      if (options.some((option) => !option.label.trim())) {
        setError(t.optionLabelRequired);
        return;
      }
    }

    setLoading(true);

    try {
      const url = field
        ? `/internal/flows/${flowId}/fields/${field.id}`
        : `/internal/flows/${flowId}/fields`;
      const optionInputs: FlowFieldOptionInput[] | undefined = isOptionFieldType(type)
        ? options.map((option) => ({
            ...(option.id ? { id: option.id } : {}),
            label: option.label.trim(),
            syncKey: optionSyncKeysEnabled ? option.syncKey.trim() || null : null,
          }))
        : undefined;
      const common = {
        name: name.trim(),
        type,
        options: optionInputs,
        isRequired,
        syncKey: trimmedSyncKey || null,
      };
      const body = field ? common : { ...common, scope };

      if (field) {
        await patch(url, body);
      } else {
        await post(url, body);
      }

      onClose(true);
    } catch (err) {
      // A 409 is a sync-key collision; the option one carries its own code.
      const optionKeyConflict = apiErrorCode(err) === "option_sync_key_conflict";
      setError(
        err instanceof ApiError && err.status === 409
          ? optionKeyConflict
            ? t.optionSyncKeyInUse
            : t.syncKeyInUse
          : t.failedToSaveField,
      );
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose(false)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{field ? t.editField : t.createNewField}</DialogTitle>
          <DialogDescription>
            {field ? t.editFieldDescription : t.createFieldDescription}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name">
                {t.fieldName} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t.fieldNamePlaceholder}
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="syncKey">{t.syncKey}</Label>
              <Input
                id="syncKey"
                value={syncKey}
                onChange={(e) => setSyncKey(e.target.value)}
                placeholder={t.syncKeyPlaceholder}
                className={cn("font-mono", syncKeyTaken && "border-destructive")}
                aria-invalid={syncKeyTaken}
                autoComplete="off"
                spellCheck={false}
                disabled={loading}
              />
              {syncKeyTaken ? (
                <p className="text-sm text-destructive">{t.syncKeyInUse}</p>
              ) : (
                <p className="text-sm text-muted-foreground">{t.syncKeyDescription}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">
                {t.fieldType} <span className="text-destructive">*</span>
              </Label>
              <Select
                value={type}
                onValueChange={(value) => handleTypeChange(value as FieldType)}
                disabled={!!field || loading}
              >
                <SelectTrigger id="type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map((ft) => (
                    <SelectItem key={ft.value} value={ft.value}>
                      {ft.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!!field && (
                <p className="text-sm text-muted-foreground">{t.fieldTypeCannotChange}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="scope">{t.fieldScope}</Label>
              <Select
                value={scope}
                onValueChange={(value) => setScope(value as FlowFieldScope)}
                disabled={!!field || loading}
              >
                <SelectTrigger id="scope">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FLOW_FIELD_SCOPES.map((value) => (
                    <SelectItem key={value} value={value}>
                      {t[SCOPE_LABEL_KEYS[value]]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                {scope === "portfolio"
                  ? t.portfolioScopeDescription
                  : scope === "goal"
                    ? t.goalScopeDescription
                    : t.globalScopeDescription}
              </p>
              {!!field && (
                <p className="text-sm text-muted-foreground">{t.fieldScopeCannotChange}</p>
              )}
            </div>

            {isOptionFieldType(type) && (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label>
                    {t.options} <span className="text-destructive">*</span>
                  </Label>
                  <Tooltip>
                    {/* Disabled items fire no pointer events — the span carries the hover. */}
                    <TooltipTrigger asChild>
                      <span tabIndex={optionSyncKeysEnabled ? undefined : 0}>
                        <ToggleGroup
                          type="single"
                          value={effectiveOptionMode}
                          onValueChange={(next) => {
                            if (next) setOptionMode(next as OptionEditMode);
                          }}
                          aria-label={t.optionModeAria}
                          disabled={!optionSyncKeysEnabled || loading}
                          className="h-8"
                        >
                          <ToggleGroupItem value="label" className="h-6 px-2.5 text-xs">
                            {t.optionModeLabel}
                          </ToggleGroupItem>
                          <ToggleGroupItem value="syncKey" className="h-6 px-2.5 text-xs">
                            {t.optionModeSyncKey}
                          </ToggleGroupItem>
                        </ToggleGroup>
                      </span>
                    </TooltipTrigger>
                    {!optionSyncKeysEnabled && (
                      <TooltipContent className="max-w-xs">
                        {t.optionSyncKeyDisabled}
                      </TooltipContent>
                    )}
                  </Tooltip>
                </div>
                <div className="space-y-2">
                  {options.map((option, index) => (
                    <FlowFieldOptionRow
                      key={option.key}
                      mode={effectiveOptionMode}
                      index={index}
                      count={options.length}
                      label={option.label}
                      syncKey={option.syncKey}
                      syncKeyTaken={isOptionKeyTaken(option)}
                      disabled={loading}
                      labels={optionRowLabels}
                      onLabelChange={(label) => handleOptionChange(index, { label })}
                      onSyncKeyChange={(syncKey) => handleOptionChange(index, { syncKey })}
                      onMove={(direction) => handleMoveOption(index, direction)}
                      onRemove={() => handleRemoveOption(index)}
                    />
                  ))}
                </div>
                {effectiveOptionMode === "label" && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddOption}
                    disabled={loading}
                  >
                    {t.addOption}
                  </Button>
                )}
                {/* Shown in either mode while a duplicate key blocks Save. */}
                {(effectiveOptionMode === "syncKey" || anyOptionKeyTaken) && (
                  <p
                    className={cn(
                      "text-xs",
                      anyOptionKeyTaken ? "text-destructive" : "text-muted-foreground",
                    )}
                  >
                    {anyOptionKeyTaken ? t.optionSyncKeyInUse : t.optionSyncKeyDescription}
                  </p>
                )}
                {optionKeysDropped && (
                  <p className="text-xs text-warning">{t.optionSyncKeysDropped}</p>
                )}
                {field && effectiveOptionMode === "label" && (
                  <p className="text-xs text-muted-foreground">{t.optionEditHint}</p>
                )}
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="required">{t.requiredField}</Label>
                <p className="text-sm text-muted-foreground">{t.requiredFieldDescription}</p>
              </div>
              <Switch
                id="required"
                checked={isRequired}
                onCheckedChange={setIsRequired}
                disabled={loading}
              />
            </div>
          </div>

          <DialogFooter>
            <DialogCancel disabled={loading}>{t.cancel}</DialogCancel>
            <Button type="submit" disabled={syncKeyTaken || anyOptionKeyTaken} loading={loading}>
              {field ? t.save : t.addField}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
